import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ReportShell } from "@/features/reports/ReportShell";
import { apiList } from "@/integrations/api/client";
import { useBranch } from "@/context/BranchContext";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { isBranchLocked } from "@/lib/roles";
import type { ReportFilters } from "@/features/reports/types";
import { format } from "date-fns";

export const Route = createFileRoute("/_authenticated/reports/asset-transfer-register")({
  component: AssetTransferRegister,
});

type Row = {
  id: string;
  transfer_number: string;
  created_at: string;
  status: string;
  asset: { asset_code: string; name: string } | null;
  from_department: { name: string } | null;
  to_department: { name: string } | null;
  from_branch: { name: string } | null;
  to_branch: { name: string } | null;
  requested_by: { full_name: string; email: string } | null;
  gate_pass: string | null;
  reason: string;
};

function useTransferRegister(filters: ReportFilters, effectiveBranch: string | null) {
  return useQuery({
    queryKey: ["report-asset-transfer-register", filters, effectiveBranch],
    queryFn: async () => {
      const params: Record<string, any> = { limit: 1000 };
      if (effectiveBranch) params.from_branch = effectiveBranch; // Or to_branch, usually from_branch is used for origin
      if (filters.status) params.status = filters.status;
      if (filters.dateFrom) params.created_at__gte = filters.dateFrom;
      if (filters.dateTo) params.created_at__lte = filters.dateTo;
      if (filters.search?.trim()) params.search = filters.search.trim();

      const res = await apiList<any>("/api/v1/transfers/transfers/", params);
      
      const rows = (res.results || []).map((t: any) => ({
        id: t.id,
        transfer_number: t.transfer_number,
        created_at: t.created_at,
        status: t.status,
        asset: t.asset ? { asset_code: t.asset.asset_code, name: t.asset.name } : null,
        from_department: t.from_department ? { name: t.from_department.name } : null,
        to_department: t.to_department ? { name: t.to_department.name } : null,
        from_branch: t.from_branch ? { name: t.from_branch.name } : null,
        to_branch: t.to_branch ? { name: t.to_branch.name } : null,
        requested_by: t.requested_by ? { full_name: t.requested_by.full_name || t.requested_by.email, email: t.requested_by.email } : null,
        gate_pass: t.gate_pass,
        reason: t.reason,
      }));
      return rows as Row[];
    },
  });
}

function AssetTransferRegister() {
  const [filters, setFilters] = useState<ReportFilters>({});
  const { currentBranchId } = useBranch();
  const { data: me } = useCurrentUser();
  const locked = isBranchLocked(me?.roles) ? (me?.profile?.branch_id ?? null) : null;
  const effectiveBranch = locked ?? filters.branchId ?? currentBranchId ?? null;
  const { data: rows = [], isLoading } = useTransferRegister(filters, effectiveBranch);

  const shaped = useMemo(
    () =>
      rows.map((r) => {
        return {
          id: r.id,
          transfer_number: r.transfer_number,
          date: r.created_at ? format(new Date(r.created_at), "dd-MM-yyyy") : "—",
          asset: r.asset ? `${r.asset.asset_code} - ${r.asset.name}` : "—",
          from: [r.from_branch?.name, r.from_department?.name].filter(Boolean).join(" / ") || "—",
          to: [r.to_branch?.name, r.to_department?.name].filter(Boolean).join(" / ") || "—",
          requested_by: r.requested_by?.full_name || "—",
          gate_pass: r.gate_pass || "—",
          reason: r.reason || "—",
          status: r.status,
        };
      }),
    [rows],
  );

  const exportRows = () =>
    shaped.map((r) => ({
      "Transfer Number": r.transfer_number,
      "Date": r.date,
      "Asset": r.asset,
      "From (Branch/Dept)": r.from,
      "To (Branch/Dept)": r.to,
      "Requested By": r.requested_by,
      "Gate Pass": r.gate_pass,
      "Reason": r.reason,
      "Status": r.status,
    }));

  return (
    <ReportShell
      id="R-20"
      title="Asset Transfer Register"
      filters={filters}
      onChange={setFilters}
      exportRows={exportRows}
      exportFilename={`Asset_Transfer_Register_${format(new Date(), "yyyyMMdd")}`}
      enabled={["branchId", "dateFrom", "dateTo", "status", "search"]}
    >
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-8 flex justify-center items-center text-muted-foreground">
              <Loader2 className="h-6 w-6 animate-spin mr-2" />
              Loading transfers...
            </div>
          ) : shaped.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">No transfers found for the selected period.</div>
          ) : (
            <div className="overflow-auto max-h-[600px]">
              <Table>
                <TableHeader className="sticky top-0 bg-background z-10 shadow-sm">
                  <TableRow>
                    <TableHead>Transfer #</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Asset</TableHead>
                    <TableHead>Origin</TableHead>
                    <TableHead>Destination</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {shaped.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell>
                        <Link to="/transfers/$transferId" params={{ transferId: r.id }} className="font-mono text-primary hover:underline">
                          {r.transfer_number}
                        </Link>
                      </TableCell>
                      <TableCell>{r.date}</TableCell>
                      <TableCell className="max-w-[200px] truncate" title={r.asset}>{r.asset}</TableCell>
                      <TableCell className="max-w-[200px] truncate" title={r.from}>{r.from}</TableCell>
                      <TableCell className="max-w-[200px] truncate" title={r.to}>{r.to}</TableCell>
                      <TableCell>
                        <Badge variant={r.status === "Completed" ? "default" : r.status.includes("Rejected") ? "destructive" : "outline"}>
                          {r.status}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </ReportShell>
  );
}
